import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, 
  LogOut,
  BookOpen,
  Loader2,
  User,
  Mail,
  Key,
  Lock,
  Unlock,
  UserX,
  UserCheck,
  Eye,
  Trash2,
  Download,
  Send,
  MessageSquare,
  Clock,
  AlertTriangle,
  CheckCircle,
  Shield,
  History,
  FileText,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

// Action Card Component
const ActionCard = ({ icon: Icon, title, description, buttonText, onClick, variant = "default", disabled = false }) => (
  <Card className="hover:shadow-md transition-shadow">
    <CardContent className="p-5">
      <div className="flex items-start gap-4">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          variant === 'danger' ? 'bg-red-100' : variant === 'warning' ? 'bg-amber-100' : 'bg-slate-100'
        }`}>
          <Icon className={`h-5 w-5 ${
            variant === 'danger' ? 'text-red-600' : variant === 'warning' ? 'text-amber-600' : 'text-slate-600'
          }`} />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-slate-900">{title}</h4>
          <p className="text-sm text-slate-500 mt-0.5">{description}</p>
          <Button 
            size="sm" 
            variant={variant === 'danger' ? 'destructive' : 'outline'}
            className="mt-3"
            onClick={onClick}
            disabled={disabled}
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Note Item Component
const NoteItem = ({ note, onDelete }) => (
  <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <p className="text-sm text-slate-700">{note.content}</p>
        <p className="text-xs text-slate-400 mt-2">
          {note.created_by} • {new Date(note.created_at).toLocaleString()}
        </p>
      </div>
      <Button variant="ghost" size="sm" onClick={() => onDelete(note.id)}>
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  </div>
);

// Email Log Item
const EmailLogItem = ({ email }) => (
  <div className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
      email.status === 'sent' ? 'bg-lime-100' : 'bg-red-100'
    }`}>
      <Mail className={`h-4 w-4 ${email.status === 'sent' ? 'text-lime-600' : 'text-red-600'}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-900 truncate">{email.subject}</p>
      <p className="text-xs text-slate-500">{new Date(email.sent_at).toLocaleString()}</p>
    </div>
    <Badge variant={email.status === 'sent' ? 'secondary' : 'destructive'} className="text-xs">
      {email.status}
    </Badge>
  </div>
);

export default function AdminUserSupport() {
  const { authAxios, logout } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('user') || '');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Typeahead state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [isFuzzy, setIsFuzzy] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentUsers, setRecentUsers] = useState(() => {
    try { return JSON.parse(localStorage.getItem('varyynt_support_recent') || '[]'); }
    catch (_) { return []; }
  });
  
  // Dialogs
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showEditEmailDialog, setShowEditEmailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showImpersonateDialog, setShowImpersonateDialog] = useState(false);
  
  // Form states
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [newNote, setNewNote] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const searchUser = useCallback(async (overrideQuery) => {
    const q = (overrideQuery ?? searchQuery).trim();
    if (!q) return;

    setLoading(true);
    try {
      const response = await authAxios.get(`/admin/support/user?query=${encodeURIComponent(q)}`);
      setSelectedUser(response.data);
      // Save to recently-viewed
      try {
        const u = response.data;
        const recent = JSON.parse(localStorage.getItem('varyynt_support_recent') || '[]');
        const display = (u.profile?.first_name && u.profile?.last_name)
          ? `${u.profile.first_name} ${u.profile.last_name}`
          : (u.email || u.id);
        const entry = { id: u.id, email: u.email, display, at: Date.now() };
        const next = [entry, ...recent.filter(r => r.id !== u.id)].slice(0, 5);
        localStorage.setItem('varyynt_support_recent', JSON.stringify(next));
        setRecentUsers(next);
      } catch (_) { /* ignore quota errors */ }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('User not found');
        setSelectedUser(null);
      } else {
        toast.error('Failed to search user');
      }
    } finally {
      setLoading(false);
    }
  }, [authAxios, searchQuery]);

  useEffect(() => {
    if (searchParams.get('user')) {
      searchUser();
    }
  }, []);

  // Debounced typeahead — calls the new ranked search endpoint
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSuggestions([]);
      setIsFuzzy(false);
      setSuggestLoading(false);
      return;
    }
    setSuggestLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await authAxios.get(`/admin/support/users/search?q=${encodeURIComponent(q)}&limit=10`);
        setSuggestions(res.data.results || []);
        setIsFuzzy(!!res.data.fuzzy);
      } catch (err) {
        setSuggestions([]);
        setIsFuzzy(false);
      } finally {
        setSuggestLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQuery, authAxios]);

  const pickSuggestion = (s) => {
    setSearchQuery(s.email || s.id);
    setShowSuggestions(false);
    searchUser(s.id);
  };

  const clearRecent = () => {
    localStorage.removeItem('varyynt_support_recent');
    setRecentUsers([]);
  };

  const handleAction = async (action, data = {}) => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      await authAxios.post(`/admin/support/user/${selectedUser.id}/action`, {
        action,
        ...data
      });
      
      toast.success(`Action completed: ${action.replace('_', ' ')}`);
      
      // Refresh user data
      const response = await authAxios.get(`/admin/support/user?query=${selectedUser.id}`);
      setSelectedUser(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || 'Action failed';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject || !emailBody) {
      toast.error('Please fill in subject and body');
      return;
    }
    
    await handleAction('send_email', { subject: emailSubject, body: emailBody });
    setShowEmailDialog(false);
    setEmailSubject('');
    setEmailBody('');
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    await handleAction('add_note', { content: newNote });
    setShowNoteDialog(false);
    setNewNote('');
  };

  const handleUpdateEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    
    await handleAction('update_email', { new_email: newEmail });
    setShowEditEmailDialog(false);
    setNewEmail('');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    await handleAction('delete_account');
    setShowDeleteDialog(false);
    setDeleteConfirm('');
    setSelectedUser(null);
    setSearchQuery('');
  };

  const handleExportData = async () => {
    try {
      const response = await authAxios.get(`/admin/support/user/${selectedUser.id}/export`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user_${selectedUser.id}_data.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
              <nav className="hidden md:flex items-center gap-1">
                <Link to="/admin" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Overview
                </Link>
                <Link to="/admin/candidates" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Candidates
                </Link>
                <Link to="/admin/job-analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Job Market
                </Link>
                <Link to="/admin/analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Analytics
                </Link>
                <Link to="/admin/market-pulse" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Market Pulse
                </Link>
                <Link to="/admin/messages" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Messages
                </Link>
                <Link to="/admin/support" className="px-4 py-2 text-sm font-medium text-lime-700 bg-lime-50 rounded-lg">
                  User Support
                </Link>
                <Link to="/admin/job-import" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Job Import
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/guide">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Admin Guide
                </Button>
              </Link>
              <Link to="/brand" target="_blank">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  Brand Guide
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-heading text-slate-900">User Support</h1>
          <p className="text-slate-500">Customer service tools and user management</p>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <form onSubmit={(e) => { e.preventDefault(); setShowSuggestions(false); searchUser(); }} className="flex gap-3 relative">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                <Input
                  placeholder="Search by name, email, title, or user ID…"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  className="pl-10"
                  data-testid="support-search-input"
                  autoComplete="off"
                />
                {suggestLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                )}

                {/* Suggestions dropdown */}
                {showSuggestions && (suggestions.length > 0 || (!searchQuery.trim() && recentUsers.length > 0)) && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto" data-testid="support-search-dropdown">
                    {/* Recently viewed (only when input is empty) */}
                    {!searchQuery.trim() && recentUsers.length > 0 && (
                      <>
                        <div className="flex items-center justify-between px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-100 bg-slate-50">
                          <span className="flex items-center gap-1.5"><Clock className="h-3 w-3" /> Recently viewed</span>
                          <button type="button" onMouseDown={(e) => { e.preventDefault(); clearRecent(); }} className="text-slate-400 hover:text-slate-600 font-normal normal-case tracking-normal">clear</button>
                        </div>
                        {recentUsers.map(r => (
                          <button
                            key={r.id}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); pickSuggestion(r); }}
                            className="w-full text-left px-3 py-2.5 hover:bg-lime-50 border-b border-slate-100 last:border-b-0 transition-colors flex items-center gap-3"
                            data-testid={`support-recent-${r.id}`}
                          >
                            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-xs font-semibold text-slate-700 shrink-0">
                              {(r.display || r.email || '?').substring(0, 2).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{r.display}</p>
                              <p className="text-xs text-slate-500 truncate">{r.email}</p>
                            </div>
                          </button>
                        ))}
                      </>
                    )}

                    {/* Search results */}
                    {searchQuery.trim() && suggestions.length > 0 && (
                      <>
                        {isFuzzy && (
                          <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100 flex items-center gap-1.5">
                            <AlertTriangle className="h-3 w-3" /> No exact match — showing closest typo-tolerant suggestions
                          </div>
                        )}
                        {suggestions.map(s => {
                          const display = (s.first_name && s.last_name)
                            ? `${s.first_name} ${s.last_name}`
                            : (s.email || s.id).split('@')[0];
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                              className="w-full text-left px-3 py-2.5 hover:bg-lime-50 border-b border-slate-100 last:border-b-0 transition-colors flex items-center gap-3"
                              data-testid={`support-suggestion-${s.id}`}
                            >
                              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-evergreen-950 to-evergreen-800 flex items-center justify-center text-xs font-semibold text-white shrink-0">
                                {(s.first_name || s.email || '?').substring(0, 2).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-slate-900 truncate">{display}</p>
                                  {s.email_verified === false && <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-amber-300 text-amber-700">Unverified</Badge>}
                                  {s.is_suspended && <Badge variant="destructive" className="text-[10px] py-0 px-1.5">Suspended</Badge>}
                                  {s.is_locked && <Badge className="text-[10px] py-0 px-1.5 bg-red-100 text-red-700 hover:bg-red-100">Locked</Badge>}
                                </div>
                                <p className="text-xs text-slate-500 truncate">{s.email}</p>
                                {s.current_title && <p className="text-xs text-slate-400 truncate">{s.current_title}</p>}
                              </div>
                              {s.match_reason && (
                                <span className="text-[10px] text-slate-400 whitespace-nowrap ml-2 hidden lg:inline">{s.match_reason}</span>
                              )}
                            </button>
                          );
                        })}
                      </>
                    )}

                    {searchQuery.trim() && !suggestLoading && suggestions.length === 0 && (
                      <div className="px-3 py-6 text-center text-sm text-slate-500">
                        No matches for "<span className="font-medium">{searchQuery}</span>"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Button type="submit" disabled={loading} className="bg-evergreen-950 hover:bg-evergreen-900" data-testid="support-search-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Open'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* User Details */}
        {selectedUser && (
          <div className="space-y-6">
            {/* User Header */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-evergreen-950 to-evergreen-800 flex items-center justify-center text-white text-xl font-heading">
                      {selectedUser.email.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-xl font-heading text-slate-900">
                        {selectedUser.profile?.first_name && selectedUser.profile?.last_name
                          ? `${selectedUser.profile.first_name} ${selectedUser.profile.last_name}`
                          : selectedUser.email.split('@')[0]}
                      </h2>
                      <p className="text-slate-500">{selectedUser.email}</p>
                      <div className="flex items-center gap-2 mt-2">
                        {selectedUser.email_verified ? (
                          <Badge className="bg-lime-100 text-lime-700">Verified</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">Unverified</Badge>
                        )}
                        {selectedUser.is_suspended && (
                          <Badge variant="destructive">Suspended</Badge>
                        )}
                        {selectedUser.is_locked && (
                          <Badge className="bg-red-100 text-red-700">Locked</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right text-sm text-slate-500">
                    <p>User ID: <code className="bg-slate-100 px-1 rounded">{selectedUser.id.substring(0, 8)}...</code></p>
                    <p>Joined: {new Date(selectedUser.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Account Actions */}
              <ActionCard
                icon={Key}
                title="Force Password Reset"
                description="Send a password reset email to the user"
                buttonText="Send Reset Email"
                onClick={() => handleAction('force_password_reset')}
                disabled={actionLoading}
              />
              
              <ActionCard
                icon={selectedUser.email_verified ? CheckCircle : Mail}
                title="Email Verification"
                description={selectedUser.email_verified ? "Email is verified" : "Manually verify user's email"}
                buttonText={selectedUser.email_verified ? "Already Verified" : "Verify Email"}
                onClick={() => handleAction('verify_email')}
                disabled={actionLoading || selectedUser.email_verified}
              />
              
              <ActionCard
                icon={selectedUser.is_locked ? Unlock : Lock}
                title={selectedUser.is_locked ? "Unlock Account" : "Lock Account"}
                description={selectedUser.is_locked ? "Allow user to log in again" : "Prevent user from logging in"}
                buttonText={selectedUser.is_locked ? "Unlock" : "Lock"}
                onClick={() => handleAction(selectedUser.is_locked ? 'unlock_account' : 'lock_account')}
                disabled={actionLoading}
                variant={selectedUser.is_locked ? "default" : "warning"}
              />
              
              <ActionCard
                icon={Eye}
                title="Impersonate User"
                description="View the app as this user (for debugging)"
                buttonText="Impersonate"
                onClick={() => setShowImpersonateDialog(true)}
                disabled={actionLoading}
              />
              
              <ActionCard
                icon={selectedUser.is_suspended ? UserCheck : UserX}
                title={selectedUser.is_suspended ? "Reactivate Account" : "Suspend Account"}
                description={selectedUser.is_suspended ? "Allow user to access their account" : "Temporarily disable user's account"}
                buttonText={selectedUser.is_suspended ? "Reactivate" : "Suspend"}
                onClick={() => handleAction(selectedUser.is_suspended ? 'unsuspend_account' : 'suspend_account')}
                disabled={actionLoading}
                variant={selectedUser.is_suspended ? "default" : "warning"}
              />
              
              <ActionCard
                icon={Mail}
                title="Edit Email"
                description="Change the user's email address"
                buttonText="Edit Email"
                onClick={() => setShowEditEmailDialog(true)}
                disabled={actionLoading}
              />
            </div>

            {/* Communication & Data */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Send Custom Email */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Send className="h-5 w-5 text-slate-600" />
                    Send Custom Email
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => setShowEmailDialog(true)} className="w-full">
                    <Mail className="h-4 w-4 mr-2" />
                    Compose Email
                  </Button>
                  
                  {/* Email Log */}
                  <div className="mt-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Recent Emails</p>
                    {selectedUser.email_log?.length > 0 ? (
                      <div className="max-h-48 overflow-y-auto">
                        {selectedUser.email_log.slice(0, 5).map((email, i) => (
                          <EmailLogItem key={i} email={email} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 py-3 text-center">No emails sent</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Support Notes */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-slate-600" />
                    Support Notes
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => setShowNoteDialog(true)}>
                    Add Note
                  </Button>
                </CardHeader>
                <CardContent>
                  {selectedUser.support_notes?.length > 0 ? (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {selectedUser.support_notes.map((note, i) => (
                        <NoteItem 
                          key={i} 
                          note={note} 
                          onDelete={() => handleAction('delete_note', { note_id: note.id })}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 py-6 text-center">No notes yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* GDPR & Danger Zone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ActionCard
                icon={Download}
                title="Export User Data (GDPR)"
                description="Download all data associated with this user"
                buttonText="Export Data"
                onClick={handleExportData}
                disabled={actionLoading}
              />
              
              <ActionCard
                icon={Trash2}
                title="Delete Account (GDPR)"
                description="Permanently delete user and all their data"
                buttonText="Delete Account"
                onClick={() => setShowDeleteDialog(true)}
                disabled={actionLoading}
                variant="danger"
              />
            </div>
          </div>
        )}

        {/* Empty State */}
        {!selectedUser && !loading && (
          <Card className="py-16">
            <CardContent className="text-center">
              <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Search for a user</h3>
              <p className="text-slate-500">Enter an email address or user ID to manage their account</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Send Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Email to User</DialogTitle>
            <DialogDescription>Send a custom email to {selectedUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject</Label>
              <Input 
                value={emailSubject} 
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject..."
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea 
                value={emailBody} 
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Write your message..."
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Support Note</DialogTitle>
            <DialogDescription>Add an internal note about this user</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea 
              value={newNote} 
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Write your note..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={actionLoading}>Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Email Dialog */}
      <Dialog open={showEditEmailDialog} onOpenChange={setShowEditEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Email Address</DialogTitle>
            <DialogDescription>Update the user's email address</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Current Email</Label>
            <p className="text-sm text-slate-500 mb-4">{selectedUser?.email}</p>
            <Label>New Email</Label>
            <Input 
              type="email"
              value={newEmail} 
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="new@email.com"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateEmail} disabled={actionLoading}>Update Email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete User Account
            </DialogTitle>
            <DialogDescription>
              This will permanently delete the user and all their data. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
            <Input 
              value={deleteConfirm} 
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAccount} 
              disabled={deleteConfirm !== 'DELETE' || actionLoading}
            >
              Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate Dialog */}
      <Dialog open={showImpersonateDialog} onOpenChange={setShowImpersonateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Impersonate User</DialogTitle>
            <DialogDescription>
              You will be logged in as this user in a new tab. Your admin session will remain active.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              Impersonating: <span className="font-medium">{selectedUser?.email}</span>
            </p>
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              This action is logged for security purposes.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImpersonateDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                handleAction('impersonate');
                setShowImpersonateDialog(false);
                toast.info('Impersonation feature coming soon - token will be generated');
              }}
              disabled={actionLoading}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open as User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
